import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import {
  CanCreateResponse,
  EmailValidateRequest,
  ForgotPasswordConfirmRequest,
  ForgotPasswordRequest,
  OtpUpdateRequest,
  PasswordParamsResponse,
  PasswordPatchRequest,
  PriorityPatchRequest,
  SecretGenerateResponse,
  UserChangeEmailRequest,
} from '@primatoo-ms-organization';
import { DataProtectionGovernanceService } from '../interface/data-protection-governance-service';
import { CheckEmailService } from '../interface/check-email-service';

import { User } from 'it/primatoo/openapi/api/org/schemas/user/User';
import { ListUserResponse } from 'it/primatoo/openapi/api/org/schemas/user/ListUserResponse';
import { CreateResponse } from 'it/primatoo/openapi/api/common/schemas/CreateResponse';
import { UpdateUserRequest } from 'it/primatoo/openapi/api/org/schemas/user/UpdateUserRequest';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { map } from 'rxjs/operators';
import { LoggedUser, PtEntityRef, PtPermissions } from '@pt/pt-models';
import { CreateUserRequest } from 'it/primatoo/openapi/api/org/schemas/user/CreateUserRequest';
import { ListUserForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/user/ListUserForMenuResponse';
import { UpsertUserColumnRequest } from 'it/primatoo/openapi/api/org/schemas/common/UpsertUserColumnRequest';
import { ApiClientUtils } from '../../api-client.utils';
import { AuthPermissionsUtils } from '../../auth-permissions.utils';
import { ChangePasswordForLoginRequest } from 'it/primatoo/openapi/api/org/schemas/user/ChangePasswordForLoginRequest';
import { ConnectAsUserRequest } from 'it/primatoo/openapi/api/adm/schemas/user/ConnectAsUserRequest';
import { ConnectAsUserResponse } from 'it/primatoo/openapi/api/adm/schemas/user/ConnectAsUserResponse';

@Injectable({
  providedIn: 'root',
})
export class UsersService
  extends DataProtectionGovernanceService
  implements CheckEmailService
{
  private readonly endpointV1: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config: ApiClientConfig,
    public httpClient: HttpClient
  ) {
    super(httpClient);
    this.endpointV1 = this.config.endPoint + '/api/org/users/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/users/';
  }

  // V2
  public connectAsUser(
    requestData: ConnectAsUserRequest
  ): Observable<LoggedUser> {
    const url = this.endpointV2 + `connect-as-user/`;
    return this.httpClient.post<ConnectAsUserResponse>(url, requestData).pipe(
      map((response: ConnectAsUserResponse) => {
        const enumeratedPermissions: PtPermissions[] =
          AuthPermissionsUtils.buildPermissions(
            response.systemUser,
            response.permissions
          );
        const result: LoggedUser = {
          id: response.id,
          name: response.name,
          email: response.email,
          surname: response.surname,
          language: response.language,
          username: response.username,
          system: response.systemUser,
          permissions: response.permissions,
          enumeratedPermissions,
        };
        return result;
      })
    );
  }

  public findAllUsers(): Observable<ListUserResponse> {
    const url = this.endpointV2;
    return this.httpClient.get<ListUserResponse>(url);
  }

  public create(user: CreateUserRequest): Observable<CreateResponse> {
    const url = this.endpointV2;
    return this.httpClient.post<CreateResponse>(url, user);
  }

  public getMenu(): Observable<ListUserForMenuResponse> {
    const url = this.endpointV2 + `menu/`;
    return this.httpClient.get<ListUserForMenuResponse>(url);
  }

  public findById(userId: number): Observable<User> {
    const url = this.endpointV2 + `${userId}/`;
    return this.httpClient.get<User>(url);
  }

  public updateUsers(
    id: number,
    requestBody: UpdateUserRequest
  ): Observable<void> {
    const url = this.endpointV2 + id + '/';
    return this.httpClient.put<void>(url, requestBody);
  }

  public getUserSelf(): Observable<User> {
    const url = this.endpointV2 + `self/`;
    return this.httpClient.get<User>(url);
  }

  public updateUserSelf(user: UpdateUserRequest): Observable<void> {
    const url = this.endpointV2 + `self/`;
    return this.httpClient.put<void>(url, user);
  }

  public findUserColumns(): Observable<ColumnWithLocalization[]> {
    const url = this.endpointV2 + `columns/`;
    return this.httpClient
      .get<ListUserColumnsResponse>(url)
      .pipe(map((m) => m.columns));
  }

  public updateUserColumns(request: UpsertUserColumnRequest): Observable<void> {
    const url = this.endpointV2 + 'user-columns';
    return this.httpClient.post<void>(url, request);
  }

  /*currentUserEmail implementato perchè il backend da errore quando la mail inserita è uguale a quella dell'utente che si sta modificando*/
  public checkEmail(
    value: string,
    currentUserEmail: string = null
  ): Observable<void> {
    const url = this.endpointV1 + `email-validate`;
    if (currentUserEmail !== value) {
      return this.httpClient.post<void>(url, { email: value });
    } else {
      return EMPTY;
    }
  }

  public getPasswordHelpParams(): Observable<PasswordParamsResponse> {
    const url = this.endpointV1 + `pw-params`;
    return this.httpClient.get<PasswordParamsResponse>(url);
  }

  public changePassword(
    oldPassword: string,
    newPassword: string
  ): Observable<void> {
    const url = this.endpointV1 + `change-password`;
    return this.httpClient.post<void>(url, { oldPassword, newPassword });
  }

  public canCreateUsers(): Observable<CanCreateResponse> {
    const url = this.endpointV1 + 'can-create';
    return this.httpClient.get<CanCreateResponse>(url);
  }

  public requiredChangePasswordRequest(
    request: ChangePasswordForLoginRequest
  ): Observable<void> {
    const url = this.endpointV2 + `required-change-password`;
    return this.httpClient.post<void>(url, request);
  }

  // V1
  public delete(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/`;
    return this.httpClient.delete<void>(url);
  }

  public setPriority(
    id: number,
    request: PriorityPatchRequest
  ): Observable<User> {
    const url = `${this.endpointV1}${id}/priority`;
    return this.httpClient.patch<User>(url, request);
  }

  public unlockUserLogin(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/unlock/`;
    return this.httpClient.post<void>(url, {});
  }

  public changeEmail(
    changeEmailRequest: UserChangeEmailRequest
  ): Observable<void> {
    const url = this.endpointV1 + `change-email`;
    return this.httpClient.post<void>(url, changeEmailRequest);
  }

  public changeEmailConfirm(token: string): Observable<void> {
    const url = this.endpointV1 + `change-email/confirm/${token}/`;
    return this.httpClient.post<void>(url, {});
  }

  public passwordValidate(pw: string): Observable<void> {
    const url = this.endpointV1 + `pw-validate`;
    return this.httpClient.post<void>(url, { pw });
  }

  public emailValidate(
    emailValidateRequest: EmailValidateRequest
  ): Observable<void> {
    const url = this.endpointV1 + `email-validate`;
    return this.httpClient.post<void>(url, emailValidateRequest);
  }

  public forgotPassword(
    domain: string,
    body: ForgotPasswordRequest,
    rcToken: string
  ): Observable<void> {
    const url = this.endpointV1 + 'forgot-password/';
    const header = ApiClientUtils.buildAndAppendHttpHeaders({
      'X-Domain': domain,
      'X-Recaptcha': rcToken,
    });
    return this.httpClient.post<void>(url, body, { headers: header });
  }

  public forgotPasswordConfirm(
    domain: string,
    token: string,
    body: ForgotPasswordConfirmRequest,
    rcToken: string
  ): Observable<void> {
    const url = this.endpointV1 + 'forgot-password/confirm/' + token + '/';
    const header = ApiClientUtils.buildAndAppendHttpHeaders({
      'X-Domain': domain,
      'X-Recaptcha': rcToken,
    });
    return this.httpClient.post<void>(url, body, { headers: header });
  }

  public patchPasswordByAdmin(
    id: number,
    request: PasswordPatchRequest
  ): Observable<void> {
    const url = `${this.endpointV1}${id}/password/`;
    return this.httpClient.patch<void>(url, request);
  }

  public getEndPointV1(): string {
    return this.endpointV1;
  }

  public exportRaw() {
    const url = this.endpointV1 + `download`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public selfOtpGenerate(): Observable<SecretGenerateResponse> {
    const url = this.endpointV1 + `self/otp/`;
    return this.httpClient.get<SecretGenerateResponse>(url);
  }

  public selfOtpUpdate(secret: OtpUpdateRequest): Observable<void> {
    const url = this.endpointV1 + `self/otp/`;
    return this.httpClient.put<void>(url, secret);
  }

  public selfOtpReset(): Observable<void> {
    const url = this.endpointV1 + `self/otp/`;
    return this.httpClient.delete<void>(url);
  }

  public userOtpReset(userId: number): Observable<void> {
    const url = this.endpointV1 + `${userId}/otp/`;
    return this.httpClient.delete<void>(url);
  }
}
